import type { ToastCreateOptions } from "@awesome.me/webawesome/dist/components/toast/toast.js";

import { type HtmxResponseContext, replaceDocument } from "~/htmx";

/** A Django message, as `hub.core.types.Toast` sends it. */
type Toast = ToastCreateOptions & { message: string };

const show = (toasts: Toast[]): void => {
  const container = document.querySelector("wa-toast");
  for (const { message, ...options } of toasts) {
    void container?.create(message, options);
  }
};

const showError = async (ctx?: HtmxResponseContext): Promise<void> => {
  const container = document.querySelector("wa-toast");
  const message = container?.dataset.errorMessage;
  if (!container || !message) {
    return;
  }
  const options = {
    variant: "danger",
    icon: "far-circle-xmark",
    duration: 0,
  } as const;
  if (!import.meta.env.DEV || !ctx) {
    void container.create(message, options);
    return;
  }
  const page = new DOMParser().parseFromString(ctx.text, "text/html");
  const item = await container.create(page.title || message, options);
  const button = document.createElement("button");
  button.type = "button";
  button.className = "c-link block";
  button.textContent = "Show error page";
  button.addEventListener("click", () => {
    replaceDocument(page);
  });
  item.append(button);
};

const isPartialError = (ctx: HtmxResponseContext): boolean =>
  ctx.response.status >= 400 && ctx.request.headers["HX-Boosted"] !== "true";

export const init = (): void => {
  show(
    JSON.parse(
      document.querySelector("wa-toast")?.dataset.toasts ?? "[]",
    ) as Toast[],
  );

  // A failed partial request shows a toast. Only boosted navigation swaps in
  // the error page.
  document.addEventListener("htmx:before:swap", (event) => {
    const { ctx } = (event as CustomEvent<{ ctx: HtmxResponseContext }>).detail;
    if (isPartialError(ctx)) {
      event.preventDefault();
    }
  });

  document.addEventListener("htmx:finally:request", (event) => {
    const { ctx } = (
      event as CustomEvent<{ ctx: Partial<HtmxResponseContext> }>
    ).detail;
    const header = ctx.response?.headers.get("X-Messages");
    if (header) {
      show(JSON.parse(header) as Toast[]);
    } else if (ctx.response && isPartialError(ctx as HtmxResponseContext)) {
      void showError(ctx as HtmxResponseContext);
    }
  });

  document.addEventListener("htmx:error", (event) => {
    const { error } = (event as CustomEvent<{ error: unknown }>).detail;
    if (error instanceof DOMException && error.name === "AbortError") {
      return;
    }
    void showError();
  });
};
